import {
  fetchOrdersByParams,
  FetchTransactionProps,
  getEOSReportBySessionId,
  getEOSReports,
  getTransactions,
} from '@/services/Reports/reports.services';
import { ClosingReportData, ReportOrder, ReportsState } from '@/types/reports.type';
import { arraysAreEqual } from '@/utils/helpers/checkArraysAreEqual';
import i18n from '@/utils/i18n';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';

export const fakeRepors: ReportOrder[] = [];

const startDate = new Date();
startDate.setHours(0, 0, 0, 0);

const endDate = new Date(startDate);
endDate.setDate(startDate.getDate() + 1);
endDate.setHours(0, 0, 0, 0);
endDate.setDate(startDate.getDate() + 1);

const initialState: ReportsState = {
  dates: {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  },
  loading: false,
  financialSummaryReport: null,
  sales: [
    {
      id: '1',
      date: '2024-01-05T00:00:00.000Z',
      reportId: '1',
    },
    {
      id: '2',
      date: '2024-01-12T00:00:00.000Z',
      reportId: '2',
    },
  ],
  EODReports: [],
  reports: [],
  totalOrders: 0,
  uniqueTaxesNames: [],
};

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    updateDates: (state, action: PayloadAction<string[]>) => {
      state.dates.startDate = action.payload[0];
      state.dates.endDate = action.payload[1];
    },
    updateReportsTotals: (state, action: PayloadAction<ClosingReportData>) => {
      console.log('actionn payload', action.payload);
      state.financialSummaryReport = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchEOSReportById.fulfilled, (state, action) => {
      state.loading = false;
      state.singleEODReport = action.payload;
    });
    builder.addCase(fetchEOSReportById.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchEOSReports.fulfilled, (state, action) => {
      state.EODReports = action.payload;
    });
    builder.addCase(fetchReports.fulfilled, (state, action) => {
      console.log('actionn payload', action.payload);
      if (action.payload) {
        state.financialSummaryReport = action.payload;
      } else {
        state.financialSummaryReport = null;
      }
      state.loading = false;
    });
    builder.addCase(fetchReports.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchReports.rejected, (state) => {
      state.loading = false;
      toast.error(i18n.t('Errors.reports_finances_error'));
    });
    (builder.addCase(fetchTransactions.fulfilled, (state, action) => {
      if (action.payload.replace) {
        state.totalOrders = action.payload.totalOrders;
        state.reports = action.payload.orders;
        state.uniqueTaxesNames = action.payload.uniqueTaxes;
      } else if (!arraysAreEqual(state.reports, action.payload.orders)) {
        const existingIds = new Set(state.reports.map((report) => report._id));
        const newReports = action.payload.orders.filter((order) => !existingIds.has(order._id));
        state.reports = [...state.reports, ...newReports];
        state.totalOrders = action.payload.totalOrders;
      }

      state.loading = false;
    }),
      builder.addCase(fetchTransactions.pending, (state) => {
        state.loading = true;
      }));
    builder.addCase(fetchTransactions.rejected, (state) => {
      state.loading = false;
      toast.error(i18n.t('Errors.reports_finances_error'));
    });
  },
});

export const { updateDates, updateReportsTotals } = reportsSlice.actions;

export default reportsSlice.reducer;

type ThunkProps = {
  companyId: string;
  dates: { startDate: string; endDate: string };
  jwtToken: string;
  stations?: string[] | [];
};

export const fetchReports = createAsyncThunk(
  'reports/fetch',
  async ({ companyId, dates, jwtToken, stations = [] }: ThunkProps) => {
    try {
      const response: ClosingReportData | undefined = await fetchOrdersByParams(
        jwtToken,
        companyId,
        dates,
        stations,
      );
      return response;
    } catch (err) {
      throw new Error('Errors.reports_finances_error');
    }
  },
);

export const fetchTransactions = createAsyncThunk(
  'reports/fetchTransactions',
  async ({
    companyId,
    dates,
    offset,
    jwtToken,
    stations,
    replace = false,
  }: FetchTransactionProps & { replace?: boolean }) => {
    try {
      const data: {
        orders: ReportOrder[];
        totalCount: number;
        uniqueTaxes: string[];
      } = await getTransactions({
        companyId,
        dates,
        offset,
        jwtToken,
        stations,
      });
      return {
        orders: data.orders,
        totalOrders: data.totalCount,
        replace,
        uniqueTaxes: data.uniqueTaxes,
      };
    } catch (err) {
      throw new Error('Errors.reports_finances_error');
    }
  },
);

export const fetchEOSReports = createAsyncThunk(
  'reports/fetchEOSReports',
  async ({ companyId, jwtToken, dates, stations = [] }: ThunkProps) => {
    const response = await getEOSReports({
      companyId,
      jwtToken,
      dates,
      stations,
    });
    return response;
  },
);

export const fetchEOSReportById = createAsyncThunk(
  'reports/fetchEOSReportById',
  async ({ sessionId, jwtToken }: { sessionId: string; jwtToken: string }) => {
    const response = await getEOSReportBySessionId(sessionId, jwtToken);
    return response;
  },
);
